import React, { useState, useEffect, UIEvent } from 'react';
import { useDispatch } from 'react-redux';
import {
  useTable,
  useSortBy,
  useFilters,
  useFlexLayout,
  usePagination,
  useRowSelect,
  Column,
  Row,
  Cell,
  TableState,
  TableInstance
} from 'react-table';

import { Filters, FilterViewModel } from 'types/Search';
import { SearchResultsCountFunction } from 'hooks/useHypotheticalSearchResultsCount';

import { clearAllFilters } from '../../redux/slices/filters';
import usePrevious from '../../hooks/usePrevious';
import * as styles from './styles/styles.module.scss';
import TableHeader from './TableHeader';
import EmptyState from './EmptyState';
import ShimmerCell from './ShimmerCell';
import AlertsDrawer from '../AlertsDrawer';
import FilterBar from '../FilterBar';
import { PaginationState, initialPaginationState, renderPaginationFillerRow, renderPaginationControls } from './paginationHelper';
import { useSelector } from '../../redux/hooks';

type TableProps = {
  columns: Column<any>[];
  data: any[];
  initialState?: Partial<TableState>;
  loading?: boolean;
  autoResetFilters?: boolean;
  resultsCount?: number;
  getAddButton?: () => JSX.Element;
  getTableHeader?: (tableInstance: TableInstance, headerClass: string) => JSX.Element;
  getRowProps?: (row: Record<string, any>) => { className?: any };
  onSelectedRowsChange?: (selectedFlatRows: Array<Row>) => void;
  getFiltersConfigurations?: (filters: Filters) => FilterViewModel[];
  searchResultsCountFunction?: SearchResultsCountFunction;
};

const defaultPropGetter = () => ({});

const SHIMMER_ROW_COUNT = 5;
export const COLUMN_ID_CHECKBOX = 'checkbox';
export const COLUMN_ID_ACTIONS = 'actions';

const Table = ({
  columns,
  data,
  initialState = {},
  loading = false,
  autoResetFilters = true,
  resultsCount = 0,
  getAddButton = null,
  getTableHeader = null,
  getRowProps = defaultPropGetter,
  onSelectedRowsChange = null,
  getFiltersConfigurations = null,
  searchResultsCountFunction = null
}: TableProps): JSX.Element => {
  const dispatch = useDispatch();
  const alertsDrawerVisible = useSelector(state => state.alertsDrawer.visible);
  const [isScrolled, setIsScrolled] = useState(false);
  initialState = initialPaginationState(initialState);
  const tableInstance = useTable(
    { columns, data, initialState, autoResetFilters },
    useFilters,
    useSortBy,
    useFlexLayout,
    usePagination,
    useRowSelect
  );
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    columns: columnInstances,
    pageOptions,
    page,
    state: { pageIndex, pageSize, selectedRowIds },
    selectedFlatRows,
    gotoPage,
    previousPage,
    nextPage,
    setPageSize,
    pageCount,
    canPreviousPage,
    canNextPage,
    visibleColumnsCount
  } = tableInstance;
  const trailNameColumn = columnInstances.find(column => ['trailName', 'trailNames'].includes(column.id));
  const resetFilters = () => {
    tableInstance.setAllFilters([]);
    dispatch(clearAllFilters());
  };
  const paginationState: PaginationState = {
    styles,
    rows,
    page,
    pageOptions,
    pageIndex,
    pageSize,
    gotoPage,
    previousPage,
    nextPage,
    setPageSize,
    pageCount,
    visibleColumnsCount,
    canPreviousPage,
    canNextPage
  };
  const prevSelectedRowIds = usePrevious(selectedRowIds);

  useEffect(() => {
    if (onSelectedRowsChange && JSON.stringify(selectedRowIds) !== JSON.stringify(prevSelectedRowIds)) {
      onSelectedRowsChange(selectedFlatRows);
    }
  }, [onSelectedRowsChange, selectedRowIds, prevSelectedRowIds, selectedFlatRows]);

  const renderLoadingOrData = () => {
    if (loading) {
      const { getHeaderGroupProps, headers } = headerGroups[0];

      return (
        <>
          {[...Array(SHIMMER_ROW_COUNT)].map((_, i) => {
            const trProps = getHeaderGroupProps();
            const otherProps = { key: `row_${i}` };
            const props = { ...trProps, ...otherProps };

            return (
              <tr {...props}>
                {headers.map(column => {
                  let short = i % 2 === 1;

                  if (column.id === COLUMN_ID_CHECKBOX) {
                    short = false;
                  }

                  return <ShimmerCell key={column.id} column={column} short={short} />;
                })}
              </tr>
            );
          })}
        </>
      );
    }

    return (
      <>
        {page.map((row: Row) => {
          prepareRow(row);
          return (
            <tr {...row.getRowProps(getRowProps(row))}>
              {row.cells.map((cell: Cell) => (
                <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
              ))}
            </tr>
          );
        })}
        {renderPaginationFillerRow(paginationState)}
      </>
    );
  };

  const renderContent = () => {
    const headerClass = isScrolled ? styles.headerShadow : '';
    const alternateHeader = getTableHeader ? getTableHeader(tableInstance, headerClass) : null;

    if (!loading && rows.length === 0) {
      return <EmptyState onResetFilters={resetFilters} />;
    }

    return (
      <table {...getTableProps()}>
        {alternateHeader || (
          <thead className={headerClass}>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                  <TableHeader key={column.id} column={column} />
                ))}
              </tr>
            ))}
          </thead>
        )}
        <tbody {...getTableBodyProps()}>{renderLoadingOrData()}</tbody>
        {renderPaginationControls(paginationState)}
      </table>
    );
  };

  const onScroll = (e: UIEvent<HTMLElement>) => {
    if (typeof window !== 'undefined') {
      setIsScrolled(e.currentTarget.scrollTop !== 0);
    }
  };

  const addButton = getAddButton ? getAddButton() : null;

  return (
    <div className={styles.wrapper}>
      <div id="modalPortal" />
      <div className={styles.filterAndActions}>
        <div className={styles.searchAndFilter}>
          {trailNameColumn && trailNameColumn.render('Filter')}
          {getFiltersConfigurations && (
            <FilterBar
              resultsCount={resultsCount}
              getFiltersConfigurations={getFiltersConfigurations}
              searchResultsCountFunction={searchResultsCountFunction}
            />
          )}
        </div>
        {addButton}
      </div>
      <div className={styles.tableWrapper}>
        {alertsDrawerVisible && <AlertsDrawer />}
        <div className={styles.table} onScroll={onScroll}>
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default Table;
